import * as THREE from 'three';
import { WORLD } from '../config';

export interface ScreenPoint {
  sx: number;
  sy: number;
}

// Renderer owns the Three.js scene: lights, free RTS camera (pan/zoom/
// rotate), and procedural terrain. Entity meshes live in entities/*.
export class Renderer {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  private container: HTMLElement;
  private raycaster = new THREE.Raycaster();
  private groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);

  // Camera state: target + distance + yaw. Pitch is fixed for readability.
  camX = WORLD / 2;
  camZ = WORLD / 2;
  camDist = 60;
  camYaw = 0;
  private readonly pitch = THREE.MathUtils.degToRad(52);

  readonly keys = new Set<string>();

  // Edge-pan state (AoE style): latest cursor position over the canvas in
  // CSS pixels, or null when the cursor is off-canvas. Consumed in update().
  pointerEdge: { x: number; y: number } | null = null;
  edgePaused = false;
  private readonly edgeMargin = 14;

  constructor(container: HTMLElement) {
    this.container = container;
    // Fast path: high-performance GPU, no stencil buffer (we never use it),
    // depth on. MSAA (antialias) cannot be toggled at runtime, so adaptive
    // quality scales pixelRatio instead — same visual win, no context loss.
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: 'high-performance',
      stencil: false,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    this.renderer.domElement.id = 'bharat-canvas';
    container.appendChild(this.renderer.domElement);

    this.scene.background = new THREE.Color(0x6f9358);
    this.scene.fog = new THREE.Fog(0x6f9358, 150, 520);

    const hemi = new THREE.HemisphereLight(0xcfe8ff, 0x3a5f2a, 0.95);
    this.scene.add(hemi);
    const sun = new THREE.DirectionalLight(0xfff2d9, 1.6);
    sun.position.set(60, 90, 30);
    this.scene.add(sun);

    this.camera = new THREE.PerspectiveCamera(
      50,
      container.clientWidth / container.clientHeight,
      0.5,
      600,
    );

    this.buildTerrain();
    this.updateCamera();
    window.addEventListener('resize', () => this.resize());
    window.addEventListener('keydown', (e) => this.keys.add(e.key.toLowerCase()));
    window.addEventListener('keyup', (e) => this.keys.delete(e.key.toLowerCase()));
  }

  get dom(): HTMLCanvasElement {
    return this.renderer.domElement;
  }

  resize(): void {
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
  }

  // Adaptive resolution: called by the frame governor when PerfMonitor
  // steps the quality level. Cheap (no context loss) and the single biggest
  // GPU saver on integrated graphics.
  setRenderScale(scale: number): void {
    this.renderer.setPixelRatio(scale);
  }

  // Pan with arrow keys; called every frame.
  update(dt: number): void {
    const speed = this.camDist * 1.1 * dt;
    const yaw = this.camYaw;
    const fwd = { x: -Math.sin(yaw), z: -Math.cos(yaw) };
    const right = { x: Math.cos(yaw), z: -Math.sin(yaw) };
    let mx = 0;
    let mz = 0;
    if (this.keys.has('arrowup')) {
      mx += fwd.x;
      mz += fwd.z;
    }
    if (this.keys.has('arrowdown')) {
      mx -= fwd.x;
      mz -= fwd.z;
    }
    if (this.keys.has('arrowleft')) {
      mx -= right.x;
      mz -= right.z;
    }
    if (this.keys.has('arrowright')) {
      mx += right.x;
      mz += right.z;
    }
    // AoE-style edge pan: cursor within a few px of the viewport edge pans
    // that way. Screen-right maps to the camera right vector, screen-up to
    // forward, so it stays correct while rotated (Q/E).
    if (this.pointerEdge && !this.edgePaused) {
      const w = this.container.clientWidth;
      const h = this.container.clientHeight;
      const m = this.edgeMargin;
      let ex = 0;
      let ez = 0;
      if (this.pointerEdge.x < m) ex -= 1;
      else if (this.pointerEdge.x > w - m) ex += 1;
      if (this.pointerEdge.y < m) ez -= 1;
      else if (this.pointerEdge.y > h - m) ez += 1;
      if (ex !== 0 || ez !== 0) {
        mx += right.x * ex - fwd.x * ez;
        mz += right.z * ex - fwd.z * ez;
      }
    }
    if (mx !== 0 || mz !== 0) {
      const l = Math.hypot(mx, mz);
      this.camX = THREE.MathUtils.clamp(this.camX + (mx / l) * speed, 0, WORLD);
      this.camZ = THREE.MathUtils.clamp(this.camZ + (mz / l) * speed, 0, WORLD);
      this.updateCamera();
    }
  }

  panBy(dxWorld: number, dzWorld: number): void {
    this.camX = THREE.MathUtils.clamp(this.camX + dxWorld, 0, WORLD);
    this.camZ = THREE.MathUtils.clamp(this.camZ + dzWorld, 0, WORLD);
    this.updateCamera();
  }

  zoom(factor: number): void {
    this.camDist = THREE.MathUtils.clamp(this.camDist * factor, 22, 110);
    this.updateCamera();
  }

  rotate(dYaw: number): void {
    this.camYaw += dYaw;
    this.updateCamera();
  }

  jumpTo(x: number, z: number): void {
    this.camX = THREE.MathUtils.clamp(x, 0, WORLD);
    this.camZ = THREE.MathUtils.clamp(z, 0, WORLD);
    this.updateCamera();
  }

  updateCamera(): void {
    const { camX, camZ, camDist, camYaw, pitch } = this;
    const ch = camDist * Math.sin(pitch);
    const cd = camDist * Math.cos(pitch);
    this.camera.position.set(
      camX + Math.sin(camYaw) * cd,
      ch,
      camZ + Math.cos(camYaw) * cd,
    );
    this.camera.lookAt(camX, 0, camZ);
  }

  // Convert a screen pixel to a point on the y=0 ground plane.
  screenToWorld(clientX: number, clientY: number): { x: number; z: number } | null {
    const rect = this.dom.getBoundingClientRect();
    const nx = ((clientX - rect.left) / rect.width) * 2 - 1;
    const ny = -((clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(new THREE.Vector2(nx, ny), this.camera);
    const hit = new THREE.Vector3();
    if (!this.raycaster.ray.intersectPlane(this.groundPlane, hit)) return null;
    return {
      x: THREE.MathUtils.clamp(hit.x, 0, WORLD),
      z: THREE.MathUtils.clamp(hit.z, 0, WORLD),
    };
  }

  // World distance covered by one screen pixel at the camera target (for
  // middle-drag panning).
  pixelsToWorld(): number {
    const dist = this.camera.position.distanceTo(
      new THREE.Vector3(this.camX, 0, this.camZ),
    );
    const h = this.container.clientHeight;
    return (2 * dist * Math.tan(THREE.MathUtils.degToRad(25))) / h;
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  dispose(): void {
    this.renderer.dispose();
  }

  // ---- terrain ----------------------------------------------------------

  private buildTerrain(): void {
    // Endless-looking outer land: a huge plane with tiled grass that melts
    // into the haze. Purely decorative — gameplay stays clamped to WORLD.
    const outerTex = makeTerrainTexture({ seed: 4451, edgeDark: false });
    outerTex.wrapS = outerTex.wrapT = THREE.RepeatWrapping;
    outerTex.repeat.set(14, 14);
    outerTex.colorSpace = THREE.SRGBColorSpace;
    const outer = new THREE.Mesh(
      new THREE.PlaneGeometry(1600, 1600),
      new THREE.MeshLambertMaterial({ map: outerTex }),
    );
    outer.rotation.x = -Math.PI / 2;
    outer.position.set(WORLD / 2, -0.12, WORLD / 2);
    this.scene.add(outer);

    // Detailed play-area ground on top (no dark rim — fog owns the horizon).
    const tex = makeTerrainTexture({ seed: 987654321, edgeDark: false });
    tex.colorSpace = THREE.SRGBColorSpace;
    const mat = new THREE.MeshLambertMaterial({ map: tex });
    const geo = new THREE.PlaneGeometry(WORLD + 80, WORLD + 80, 1, 1);
    const ground = new THREE.Mesh(geo, mat);
    ground.rotation.x = -Math.PI / 2;
    ground.position.set(WORLD / 2, -0.05, WORLD / 2);
    this.scene.add(ground);

    // Playable-boundary: a ring of low dirt hills just outside the map
    // edge. Gameplay stays clamped to the WORLD square on the server; the
    // hills are purely visual so the limit reads as terrain, not a fence.
    {
      // Squashed dome: wide base, gentle rise — reads as a hill, never a peak.
      const hillGeo = new THREE.SphereGeometry(1, 8, 5, 0, Math.PI * 2, 0, Math.PI / 2);
      const COUNT = 256;
      const hills = new THREE.InstancedMesh(
        hillGeo,
        new THREE.MeshLambertMaterial({ color: 0xffffff, flatShading: true }),
        COUNT,
      );
      const dummy = new THREE.Object3D();
      const tint = new THREE.Color();
      let mseed = 777;
      const mrnd = () => {
        mseed = (mseed * 1103515245 + 12345) & 0x7fffffff;
        return mseed / 0x7fffffff;
      };
      let mi = 0;
      // Two overlapping rows per edge: low inner mounds + slightly taller
      // outer ridge. Wide radii + tight spacing = continuous rolling bank.
      const rows = [
        { off: 8, hMin: 2.5, hMax: 4.5, rMin: 7, rMax: 10 },
        { off: 18, hMin: 3.5, hMax: 6, rMin: 9, rMax: 12 },
      ];
      const putHill = (x: number, z: number, r: number, h: number) => {
        if (mi >= COUNT) return;
        dummy.position.set(x, -0.3, z);
        dummy.rotation.set(0, mrnd() * Math.PI, 0);
        // Slightly elliptical footprint so the ridge doesn't look stamped.
        dummy.scale.set(r * (0.9 + mrnd() * 0.3), h, r * (0.9 + mrnd() * 0.3));
        dummy.updateMatrix();
        hills.setMatrixAt(mi, dummy.matrix);
        // Dirt palette: browns from dry tan to dark loam, hint of grass.
        hills.setColorAt(mi, tint.setHSL(0.07 + mrnd() * 0.05, 0.3 + mrnd() * 0.18, 0.24 + mrnd() * 0.13));
        mi++;
      };
      for (const row of rows) {
        const step = 6;
        for (let t = -row.off; t <= WORLD + row.off; t += step) {
          const j = () => (mrnd() - 0.5) * 3;
          const r = row.rMin + mrnd() * (row.rMax - row.rMin);
          const h = row.hMin + mrnd() * (row.hMax - row.hMin);
          putHill(t + j(), -row.off + j(), r, h); // north
          putHill(t + j(), WORLD + row.off + j(), r, h); // south
          putHill(-row.off + j(), t + j(), r, h); // west
          putHill(WORLD + row.off + j(), t + j(), r, h); // east
        }
      }
      hills.count = mi;
      hills.instanceMatrix.needsUpdate = true;
      if (hills.instanceColor) hills.instanceColor.needsUpdate = true;
      hills.frustumCulled = false;
      this.scene.add(hills);
    }

    // Granite outcrops: clustered boulders with per-rock shading on dirt
    // beds (decorative — not minable). Centers keep clear of base sites
    // (mirrors the server's spawn ring) so rocks never swallow a TC.
    const rockGeo = new THREE.DodecahedronGeometry(0.9, 0);
    const rockMat = new THREE.MeshLambertMaterial({ color: 0xffffff });
    const rocks = new THREE.InstancedMesh(rockGeo, rockMat, 90);
    const beds = new THREE.InstancedMesh(
      new THREE.CircleGeometry(1.9, 10),
      new THREE.MeshLambertMaterial({ color: 0x5c5648 }),
      30,
    );
    const dummy = new THREE.Object3D();
    const tint = new THREE.Color();
    let seed = 12345;
    const rnd = () => {
      seed = (seed * 1103515245 + 12345) & 0x7fffffff;
      return seed / 0x7fffffff;
    };
    const clearOfBase = (x: number, z: number) => {
      const spots: Array<[number, number]> = [
        [28, 28], [WORLD - 28, WORLD - 28],
        [WORLD - 28, 28], [28, WORLD - 28],
        [WORLD / 2, 22], [WORLD / 2, WORLD - 22],
        [22, WORLD / 2], [WORLD - 22, WORLD / 2],
      ];
      return spots.every(([sx, sz]) => Math.hypot(x - sx, z - sz) > 12);
    };
    let ri = 0;
    for (let s = 0; s < 30; s++) {
      let cx = 0;
      let cz = 0;
      for (let tries = 0; tries < 20; tries++) {
        const px = rnd() * WORLD;
        const pz = rnd() * WORLD;
        if (clearOfBase(px, pz)) {
          cx = px;
          cz = pz;
          break;
        }
        cx = px;
        cz = pz;
      }
      // Dirt bed grounding the outcrop.
      dummy.position.set(cx, 0.015, cz);
      dummy.rotation.set(-Math.PI / 2, 0, 0);
      dummy.scale.setScalar(0.8 + rnd() * 0.6);
      dummy.updateMatrix();
      beds.setMatrixAt(s, dummy.matrix);
      // Main boulder + two satellites, granite greys with warm variance.
      for (let k = 0; k < 3 && ri < 90; k++) {
        const a = rnd() * Math.PI * 2;
        const r = k === 0 ? 0 : 0.9 + rnd() * 0.9;
        const sc = k === 0 ? 0.7 + rnd() * 0.9 : 0.3 + rnd() * 0.4;
        dummy.position.set(cx + Math.cos(a) * r, 0.2 * sc, cz + Math.sin(a) * r);
        dummy.scale.setScalar(sc);
        dummy.rotation.set(0, rnd() * Math.PI, 0);
        dummy.updateMatrix();
        rocks.setMatrixAt(ri, dummy.matrix);
        rocks.setColorAt(ri, tint.setHSL(0.08, 0.04 + rnd() * 0.09, 0.32 + rnd() * 0.26));
        ri++;
      }
    }
    rocks.instanceMatrix.needsUpdate = true;
    if (rocks.instanceColor) rocks.instanceColor.needsUpdate = true;
    this.scene.add(rocks);
    this.scene.add(beds);

    // Rolling knolls: small grassy mounds scattered over the open map for
    // relief. Kept deliberately low (≤1.4) so units, trees and buildings
    // always poke out — pure decoration, never cover. Spawn clearings stay
    // flat so Town Centers sit level.
    {
      const knollGeo = new THREE.SphereGeometry(1, 9, 5, 0, Math.PI * 2, 0, Math.PI / 2);
      const COUNT = 32;
      const knolls = new THREE.InstancedMesh(
        knollGeo,
        new THREE.MeshLambertMaterial({ color: 0xffffff, flatShading: true }),
        COUNT,
      );
      let kseed = 4242;
      const krnd = () => {
        kseed = (kseed * 1103515245 + 12345) & 0x7fffffff;
        return kseed / 0x7fffffff;
      };
      const placed: Array<{ x: number; z: number; r: number }> = [];
      let ki = 0;
      for (let tries = 0; tries < 300 && ki < COUNT; tries++) {
        const r = 2.2 + krnd() * 2.4;
        const px = 6 + krnd() * (WORLD - 12);
        const pz = 6 + krnd() * (WORLD - 12);
        if (!clearOfBase(px, pz)) continue;
        if (placed.some((p) => Math.hypot(px - p.x, pz - p.z) < (r + p.r) * 0.75)) continue;
        placed.push({ x: px, z: pz, r });
        const h = 0.7 + krnd() * 0.7;
        dummy.position.set(px, -0.08, pz);
        dummy.rotation.set(0, krnd() * Math.PI, 0);
        dummy.scale.set(r * (0.85 + krnd() * 0.3), h, r * (0.85 + krnd() * 0.3));
        dummy.updateMatrix();
        knolls.setMatrixAt(ki, dummy.matrix);
        // Grassy with dirt variance, blended to the ground palette.
        if (krnd() < 0.55) knolls.setColorAt(ki, tint.setHSL(0.26 + krnd() * 0.05, 0.38, 0.3 + krnd() * 0.08));
        else knolls.setColorAt(ki, tint.setHSL(0.08 + krnd() * 0.03, 0.32 + krnd() * 0.12, 0.28 + krnd() * 0.08));
        ki++;
      }
      knolls.count = ki;
      knolls.instanceMatrix.needsUpdate = true;
      if (knolls.instanceColor) knolls.instanceColor.needsUpdate = true;
      knolls.frustumCulled = false;
      this.scene.add(knolls);
    }
  }
}

// Procedural grass texture. edgeDark vignettes the rim (legacy void look);
// the live map leaves it off and lets fog own the horizon.
function makeTerrainTexture(opts: { seed: number; edgeDark: boolean }): THREE.CanvasTexture {
  const S = 1024;
  const cv = document.createElement('canvas');
  cv.width = cv.height = S;
  const ctx = cv.getContext('2d')!;
  const base = ctx.createLinearGradient(0, 0, S, S);
  base.addColorStop(0, '#4d7c3a');
  base.addColorStop(0.5, '#557f36');
  base.addColorStop(1, '#476f33');
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, S, S);
  let seed = opts.seed;
  const rnd = () => {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    return seed / 0x7fffffff;
  };
  // Dirt / grass patches.
  for (let i = 0; i < 260; i++) {
    const r = 8 + rnd() * 46;
    ctx.fillStyle =
      rnd() < 0.45 ? 'rgba(122, 96, 58, 0.20)' : 'rgba(38, 84, 32, 0.22)';
    ctx.beginPath();
    ctx.arc(rnd() * S, rnd() * S, r, 0, Math.PI * 2);
    ctx.fill();
  }
  // Speckle.
  for (let i = 0; i < 5200; i++) {
    const v = rnd();
    ctx.fillStyle =
      v < 0.5 ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,240,0.06)';
    ctx.fillRect(rnd() * S, rnd() * S, 2, 2);
  }
  // Darken rim (only for standalone vignette use).
  if (opts.edgeDark) {
    const edge = ctx.createRadialGradient(
      S / 2, S / 2, S * 0.32, S / 2, S / 2, S * 0.72,
    );
    edge.addColorStop(0, 'rgba(0,0,0,0)');
    edge.addColorStop(1, 'rgba(0,0,0,0.42)');
    ctx.fillStyle = edge;
    ctx.fillRect(0, 0, S, S);
  }
  const tex = new THREE.CanvasTexture(cv);
  tex.anisotropy = 4;
  return tex;
}
